import { useEffect, useMemo, useState } from "react";
import { ArrowRight, ChevronLeft, ChevronRight, Download, FileText, Plus } from "lucide-react";
import { toast } from "sonner";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import {
  approveInvoice,
  listItemTemplates,
  openInvoicePdf,
  rejectInvoice,
  submitInvoiceForApproval,
} from "@/features/invoices/api";
import { ConfirmInvoiceDialog } from "@/features/invoices/components/confirm-invoice-dialog";
import { InvoicePreview } from "@/features/invoices/components/invoice-preview";
import { TERMS_LABELS } from "@/features/invoices/lib/terms";
import type { InvoiceItemTemplate, InvoiceTerms } from "@/features/invoices/types";
import { Loader } from "@/components/common/loader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { InvoiceStatusBadge } from "@/features/invoices/components/invoice-status-badge";
import { LineItemRow } from "@/features/invoices/components/line-item-row";
import { LineItemStarterRow } from "@/features/invoices/components/line-item-starter-row";
import { withTotals } from "@/features/invoices/lib/invoice-calc";
import { nextInvoiceNumber } from "@/features/invoices/lib/invoice-number";
import { useInvoiceStore } from "@/features/invoices/store/invoice-store";
import { useClient } from "@/features/clients/hooks/use-client";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { isPlatformUser } from "@/features/auth/roles";
import { formatCents } from "@/lib/utils";

export default function ClientInvoiceEditPage() {
  const { id, invoiceId } = useParams<{ id: string; invoiceId: string }>();
  const { client, loading: clientLoading } = useClient(id);
  const clientId = client?.id;
  const navigate = useNavigate();
  const firmName = useAuthStore((s) => s.user?.firm?.name) ?? "NSI Capitals";

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [templates, setTemplates] = useState<InvoiceItemTemplate[]>([]);

  const loadClient = useInvoiceStore((s) => s.loadClient);
  const storeClientId = useInvoiceStore((s) => s.clientId);
  const loading = useInvoiceStore((s) => s.loading);
  const allInvoices = useInvoiceStore((s) => s.invoices);
  const invoice = useInvoiceStore((s) =>
    s.invoices.find((inv) => inv.id === invoiceId && inv.clientId === clientId),
  );
  // Select the raw arrays (stable references) and filter in the render body —
  // filtering inside the selector itself returns a new array every call, which
  // trips React's infinite-update guard against Zustand's useSyncExternalStore.
  const allLineItems = useInvoiceStore((s) => s.lineItems);
  const allInvoicePayments = useInvoiceStore((s) => s.invoicePayments);
  const payments = useInvoiceStore((s) => s.payments);
  const lineItems = allLineItems.filter((item) => item.invoiceId === invoiceId);
  const invoicePayments = allInvoicePayments.filter((ip) => ip.invoiceId === invoiceId);
  const updateInvoiceHeader = useInvoiceStore((s) => s.updateInvoiceHeader);
  const deleteDraftInvoice = useInvoiceStore((s) => s.deleteDraftInvoice);
  const voidInvoice = useInvoiceStore((s) => s.voidInvoice);
  const addLineItem = useInvoiceStore((s) => s.addLineItem);
  const updateLineItem = useInvoiceStore((s) => s.updateLineItem);
  const deleteLineItem = useInvoiceStore((s) => s.deleteLineItem);
  const refresh = useInvoiceStore((s) => s.refresh);

  // CD→SA approval controls. A Country Director submits a draft; a platform
  // admin (Super Admin) approves or rejects it. NOT_SUBMITTED keeps the legacy
  // direct "Confirm & send" path.
  const user = useAuthStore((s) => s.user);
  const isPlatform = isPlatformUser(user);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectNote, setRejectNote] = useState("");

  async function runApproval(fn: () => Promise<unknown>, okMsg: string) {
    try {
      await fn();
      await refresh();
      toast.success(okMsg);
    } catch (err) {
      toast.error((err as { message?: string })?.message ?? "Couldn't complete that action.");
    }
  }

  useEffect(() => {
    if (clientId) loadClient(clientId);
  }, [clientId, loadClient]);

  useEffect(() => {
    listItemTemplates()
      .then(setTemplates)
      .catch(() => setTemplates([]));
  }, []);

  // The client has credit to offer at confirm time if any payment still has a
  // refundable/unapplied balance.
  const hasOpenCredit = useMemo(
    () => payments.some((p) => p.clientId === clientId && p.refundableCents > 0),
    [payments, clientId],
  );

  if (clientLoading) {
    return <Loader className="mt-10" />;
  }
  if (!client) {
    return <Navigate to="/clients" replace />;
  }
  // Wait until this client's billing data has loaded before deciding the
  // invoice doesn't exist (otherwise a deep link would bounce before the fetch).
  if (storeClientId !== client.id || loading) {
    return <Loader className="mt-10" />;
  }
  if (!invoice) {
    return <Navigate to={`/client/${client.id}/invoices`} replace />;
  }

  const totals = withTotals(invoice, lineItems, invoicePayments);
  const isDraft = invoice.status === "DRAFT";
  const invoicesHref = `/client/${client.id}/invoices`;

  async function handleDeleteDraft() {
    await deleteDraftInvoice(invoice!.id);
    navigate(invoicesHref);
  }

  async function handleViewPdf() {
    const tab = window.open("", "_blank");
    try {
      await openInvoicePdf(invoice!.id, tab);
    } catch {
      tab?.close();
      toast.error("Couldn't generate the PDF.");
    }
  }

  function handleAddFromTemplate(templateId: string) {
    const template = templates.find((t) => t.id === templateId);
    if (!template) return;
    addLineItem(invoice!.id, {
      description: template.body || template.name,
      quantity: 1,
      unitPriceCents: template.amountCents,
    });
  }

  const appliedPayments = invoicePayments
    .map((ip) => ({ ip, payment: payments.find((p) => p.id === ip.paymentId) }))
    .filter((row): row is { ip: (typeof invoicePayments)[number]; payment: NonNullable<(typeof row)["payment"]> } =>
      Boolean(row.payment),
    );

  return (
    <div className="flex flex-col gap-6">
      {/* Breadcrumb */}
      <nav
        aria-label="Breadcrumb"
        className="text-muted-foreground flex flex-wrap items-center gap-2 text-[13px]"
      >
        <Link to="/clients" className="hover:text-foreground font-medium">
          Clients
        </Link>
        <ChevronRight className="text-border size-3.5" />
        <Link to={`/client/${client.id}/overview`} className="hover:text-foreground font-medium">
          {client.name}
        </Link>
        <ChevronRight className="text-border size-3.5" />
        <Link to={invoicesHref} className="hover:text-foreground font-medium">
          Invoices
        </Link>
        <ChevronRight className="text-border size-3.5" />
        <span aria-current="page" className="text-foreground truncate font-semibold">
          {invoice.invoiceNumber}
        </span>
      </nav>

      {/* Title bar */}
      <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
        <div className="flex items-center gap-3.5">
          <Button asChild variant="outline" size="icon" className="size-[42px] shrink-0" aria-label="Back to invoices">
            <Link to={invoicesHref}>
              <ChevronLeft />
            </Link>
          </Button>
          <div className="flex flex-col gap-0.5">
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="text-[28px] leading-tight font-semibold tracking-tight">
                Invoice {invoice.invoiceNumber}
              </h1>
              <InvoiceStatusBadge status={invoice.status} />
            </div>
            <span className="text-muted-foreground text-[13px]">
              For{" "}
              <Link to={`/client/${client.id}/overview`} className="text-info font-semibold hover:underline">
                {client.name}
              </Link>
              {isDraft && " · unsaved changes are kept automatically"}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isDraft ? (
            <>
              <AlertDialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="text-destructive hover:text-destructive">
                    Delete draft
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this draft invoice?</AlertDialogTitle>
                    <AlertDialogDescription>This can&apos;t be undone.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDeleteDraft}>Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <Button variant="outline" onClick={handleViewPdf}>
                <Download /> Download PDF
              </Button>
              {(() => {
                const approval = invoice.approvalStatus ?? "NOT_SUBMITTED";
                const pending = approval === "PENDING_APPROVAL";
                const rejected = approval === "REJECTED";
                return (
                  <>
                    {approval !== "NOT_SUBMITTED" && (
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                          approval === "APPROVED"
                            ? "bg-success-soft text-success"
                            : rejected
                              ? "bg-destructive/10 text-destructive"
                              : "bg-warning-soft text-warning"
                        }`}
                        title={rejected ? invoice.approvalNote : undefined}
                      >
                        {pending ? "Pending approval" : rejected ? "Rejected" : "Approved"}
                      </span>
                    )}
                    {/* CD submits for approval (or resubmits a rejected one). */}
                    {!isPlatform && (approval === "NOT_SUBMITTED" || rejected) && (
                      <Button
                        variant="outline"
                        onClick={() =>
                          runApproval(
                            () => submitInvoiceForApproval(invoice.id),
                            "Submitted for approval.",
                          )
                        }
                      >
                        {rejected ? "Resubmit for approval" : "Submit for approval"}
                      </Button>
                    )}
                    {/* Super Admin approves / rejects a pending submission. */}
                    {isPlatform && pending && (
                      <>
                        <Button
                          variant="outline"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setRejectOpen(true)}
                        >
                          Reject
                        </Button>
                        <Button
                          onClick={() =>
                            runApproval(() => approveInvoice(invoice.id), "Invoice approved.")
                          }
                        >
                          Approve
                        </Button>
                      </>
                    )}
                    {/* Confirm & send — blocked while awaiting/denied approval. */}
                    <Button
                      variant="cta"
                      disabled={pending || rejected}
                      title={
                        pending
                          ? "Awaiting Super-Admin approval"
                          : rejected
                            ? "Rejected — resubmit for approval first"
                            : undefined
                      }
                      onClick={() => setConfirmOpen(true)}
                    >
                      Confirm &amp; send <ArrowRight />
                    </Button>
                  </>
                );
              })()}
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handleViewPdf}>
                <FileText /> View PDF
              </Button>
              {invoice.status === "OPEN" && (
                <Button variant="destructive" onClick={() => voidInvoice(invoice.id)}>
                  Void Invoice
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Editor + preview */}
      <div className="grid items-start gap-6 lg:grid-cols-2">
        {/* Editor */}
        <div className="flex min-w-0 flex-col gap-5">
          {/* Details */}
          <Card className="flex flex-col gap-4 p-6">
            <h2 className="text-[17px] font-semibold tracking-[-0.01em]">Details</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label htmlFor="inv-number">Invoice number</Label>
                <Input id="inv-number" value={invoice.invoiceNumber} readOnly className="bg-muted text-muted-foreground" />
                {isDraft && (
                  <span className="text-muted-foreground text-xs">
                    Assigned as #{nextInvoiceNumber(allInvoices)} on confirm
                  </span>
                )}
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="inv-date">Invoice date</Label>
                {isDraft ? (
                  <Input
                    id="inv-date"
                    type="date"
                    value={invoice.invoiceDate}
                    onChange={(e) => updateInvoiceHeader(invoice.id, { invoiceDate: e.target.value })}
                  />
                ) : (
                  <p className="text-foreground pt-2">{invoice.invoiceDate}</p>
                )}
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="inv-terms">Terms</Label>
                {isDraft ? (
                  <Select
                    value={invoice.terms}
                    onValueChange={(v) => updateInvoiceHeader(invoice.id, { terms: v as InvoiceTerms })}
                  >
                    <SelectTrigger id="inv-terms" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(TERMS_LABELS) as InvoiceTerms[]).map((terms) => (
                        <SelectItem key={terms} value={terms}>
                          {TERMS_LABELS[terms]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-foreground pt-2">{TERMS_LABELS[invoice.terms]}</p>
                )}
              </div>
              <div className="grid gap-1.5">
                <Label>Due date</Label>
                <p className="text-muted-foreground pt-2">{invoice.dueDate}</p>
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="inv-billto">Bill to</Label>
              {isDraft ? (
                <Textarea
                  id="inv-billto"
                  rows={3}
                  value={invoice.billTo}
                  onChange={(e) => updateInvoiceHeader(invoice.id, { billTo: e.target.value })}
                />
              ) : (
                <p className="text-foreground whitespace-pre-line">{invoice.billTo}</p>
              )}
            </div>
          </Card>

          {/* Line items */}
          <Card className="gap-0 overflow-hidden p-0">
            <div className="flex items-center justify-between gap-3 px-6 pt-5 pb-3.5">
              <h2 className="text-[17px] font-semibold tracking-[-0.01em]">Line items</h2>
              <span className="text-muted-foreground text-xs">{lineItems.length} items</span>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead>Description</TableHead>
                    <TableHead className="w-24 text-right">Qty</TableHead>
                    <TableHead className="w-32 text-right">Unit price</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    {isDraft && <TableHead className="w-10" />}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lineItems.map((item) =>
                    isDraft ? (
                      <LineItemRow
                        key={item.id}
                        item={item}
                        onUpdate={(patch) => updateLineItem(item.id, patch)}
                        onDelete={() => deleteLineItem(item.id)}
                      />
                    ) : (
                      <TableRow key={item.id}>
                        <TableCell>{item.description}</TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                        <TableCell className="text-right">{formatCents(item.unitPriceCents)}</TableCell>
                        <TableCell className="text-right">{formatCents(item.extendedAmtCents)}</TableCell>
                      </TableRow>
                    ),
                  )}
                  {lineItems.length === 0 &&
                    (isDraft ? (
                      <LineItemStarterRow onCreate={(item) => addLineItem(invoice.id, item)} />
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} className="text-muted-foreground text-center">
                          No line items yet.
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>

            {isDraft && (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => addLineItem(invoice.id, { description: "", quantity: 1, unitPriceCents: 0 })}
                  className="text-info bg-muted/40 hover:bg-accent hover:text-info h-12 w-full justify-center gap-2 rounded-none border-t border-dashed font-semibold"
                >
                  <Plus /> Add line item
                </Button>
                {templates.length > 0 && (
                  <div className="flex justify-center border-t px-6 py-2.5">
                    <Select value="" onValueChange={handleAddFromTemplate}>
                      <SelectTrigger className="h-8 w-56 text-sm" aria-label="Add from template">
                        <SelectValue placeholder="Add from template…" />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.name} — {formatCents(t.amountCents)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}

            {/* Totals */}
            <div className="flex flex-col gap-2.5 border-t px-6 py-5">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="tabular-nums">{formatCents(totals.subtotalCents)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <div className="text-muted-foreground flex items-center gap-2.5">
                  <Label htmlFor="inv-tax" className="font-normal">
                    Tax
                  </Label>
                  {isDraft ? (
                    <span className="border-input bg-background inline-flex h-9 items-center overflow-hidden rounded-md border">
                      <Input
                        id="inv-tax"
                        type="number"
                        min={0}
                        max={100}
                        step="any"
                        value={invoice.taxPercent}
                        onChange={(e) => updateInvoiceHeader(invoice.id, { taxPercent: Number(e.target.value) || 0 })}
                        className="h-full w-14 rounded-none border-0 text-right shadow-none focus-visible:ring-0"
                      />
                      <span className="text-muted-foreground pr-2.5">%</span>
                    </span>
                  ) : (
                    <span>({invoice.taxPercent}%)</span>
                  )}
                </div>
                <span className="tabular-nums">{formatCents(totals.taxAmtCents)}</span>
              </div>
              <div className="bg-border my-1 h-px" />
              <div className="flex items-baseline justify-between">
                <span className="text-[15px] font-semibold">Total due</span>
                <span className="font-heading text-2xl font-bold tracking-tight tabular-nums">
                  {formatCents(totals.totalCents)}
                </span>
              </div>
              {/* Internal affiliate commission (NSI house book only) — captured at
                  confirm; never shown on the client-facing invoice. */}
              {(invoice.referralCommissionCents ?? 0) > 0 && (
                <div className="text-muted-foreground mt-1 flex items-center justify-between border-t pt-2.5 text-xs">
                  <span>
                    Affiliate commission
                    {invoice.referralName ? ` · ${invoice.referralName}` : ""}
                    {invoice.referralCommissionPercent
                      ? ` (${invoice.referralCommissionPercent}%)`
                      : ""}
                    <span className="ml-1 italic">— internal</span>
                  </span>
                  <span className="tabular-nums">
                    {formatCents(invoice.referralCommissionCents ?? 0)}
                  </span>
                </div>
              )}
            </div>
          </Card>

          {/* Note to client */}
          {isDraft ? (
            <Card className="flex flex-col gap-3 p-6">
              <h2 className="text-[17px] font-semibold tracking-[-0.01em]">Note to client</h2>
              <Textarea
                id="inv-note"
                rows={2}
                value={invoice.noteToClient}
                onChange={(e) => updateInvoiceHeader(invoice.id, { noteToClient: e.target.value })}
                placeholder="Optional — appears at the bottom of the invoice. e.g. Thank you for trusting us with your case."
              />
            </Card>
          ) : (
            invoice.noteToClient && (
              <Card className="flex flex-col gap-2 p-6">
                <h2 className="text-[17px] font-semibold tracking-[-0.01em]">Note to client</h2>
                <p className="text-muted-foreground whitespace-pre-line">{invoice.noteToClient}</p>
              </Card>
            )
          )}
        </div>

        {/* Preview */}
        <InvoicePreview invoice={invoice} lineItems={lineItems} totals={totals} firmName={firmName} />
      </div>

      {(invoice.status === "OPEN" || invoice.status === "PAID") && (
        <Card className="gap-0 overflow-hidden p-0">
          <div className="border-b p-4">
            <h2 className="text-foreground font-semibold">Payments</h2>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40">
                <TableHead>Date</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead className="text-right">Applied</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {appliedPayments.map(({ ip, payment }) => (
                <TableRow key={ip.id}>
                  <TableCell>{payment.paymentDate}</TableCell>
                  <TableCell>{payment.source}</TableCell>
                  <TableCell>{payment.reference || "—"}</TableCell>
                  <TableCell className="text-right">{formatCents(ip.amountCents)}</TableCell>
                </TableRow>
              ))}
              {appliedPayments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-muted-foreground text-center">
                    No payments applied yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      )}

      <ConfirmInvoiceDialog
        invoiceId={invoice.id}
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        hasOpenCredit={hasOpenCredit}
      />

      {/* Super-Admin reject-with-reason dialog. */}
      <AlertDialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject this invoice?</AlertDialogTitle>
            <AlertDialogDescription>
              It goes back to the Country Director to revise and resubmit. Add a reason so they
              know what to fix.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            placeholder="e.g. The hourly rate is wrong — should be $250."
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const note = rejectNote.trim();
                setRejectOpen(false);
                setRejectNote("");
                void runApproval(() => rejectInvoice(invoice.id, note), "Invoice rejected.");
              }}
            >
              Reject
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
